import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import Navigation from '@/components/Navigation';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { BookOpen, Search, FileText, ShieldAlert, Download, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function DocsPage({ user }) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [docs, setDocs] = useState([]);
  const [activeSlug, setActiveSlug] = useState(searchParams.get('doc') || 'README');
  const [content, setContent] = useState('');
  const [loadingList, setLoadingList] = useState(true);
  const [loadingDoc, setLoadingDoc] = useState(false);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [pdfPages, setPdfPages] = useState(0);   // total page count when viewer is open
  const [refreshing, setRefreshing] = useState(false);
  const [refreshProgress, setRefreshProgress] = useState(0);
  const [refreshStage, setRefreshStage] = useState('');
  const token = typeof window !== 'undefined'
    ? (sessionStorage.getItem('session_token') || localStorage.getItem('sll_session_token'))
    : null;
  const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};

  useEffect(() => {
    if (!user || user.role !== 'admin') return;
    fetch(`${BACKEND_URL}/api/admin/docs`, { credentials: 'include', headers: authHeaders })
      .then(r => r.ok ? r.json() : Promise.reject(r))
      .then(d => setDocs(d.docs || []))
      .catch(() => setError('Could not load doc list'))
      .finally(() => setLoadingList(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    if (!user || user.role !== 'admin' || !activeSlug) return;
    setLoadingDoc(true);
    setError('');
    fetch(`${BACKEND_URL}/api/admin/docs/${activeSlug}`, { credentials: 'include', headers: authHeaders })
      .then(r => r.ok ? r.json() : Promise.reject(r))
      .then(d => setContent(d.content || ''))
      .catch(() => setError('Could not load this document'))
      .finally(() => setLoadingDoc(false));
    setSearchParams({ doc: activeSlug }, { replace: true });
    if (typeof window !== 'undefined') window.scrollTo({ top: 0, behavior: 'smooth' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSlug, user]);

  if (!user) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navigation user={user} />
        <main className="pt-24 px-6">
          <div className="container mx-auto max-w-2xl">
            <Card className="p-8 text-center">
              <ShieldAlert className="h-10 w-10 text-amber-500 mx-auto mb-3" />
              <h2 className="text-lg font-bold text-slate-800 mb-2">Sign in required</h2>
              <p className="text-sm text-slate-600 mb-4">This area is admin-only.</p>
              <Button onClick={() => navigate('/login')} data-testid="docs-login-btn">Sign in</Button>
            </Card>
          </div>
        </main>
      </div>
    );
  }

  if (user.role !== 'admin') {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navigation user={user} />
        <main className="pt-24 px-6">
          <div className="container mx-auto max-w-2xl">
            <Card className="p-8 text-center" data-testid="docs-forbidden">
              <ShieldAlert className="h-10 w-10 text-red-500 mx-auto mb-3" />
              <h2 className="text-lg font-bold text-slate-800 mb-2">Restricted</h2>
              <p className="text-sm text-slate-600">This documentation is only available to administrators.</p>
            </Card>
          </div>
        </main>
      </div>
    );
  }

  const filtered = docs.filter(d => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return (d.title || '').toLowerCase().includes(q) || (d.slug || '').toLowerCase().includes(q);
  });

  return (
    <div className="min-h-screen bg-slate-50">
      <Navigation user={user} />
      <main className="pt-24 pb-16 px-4 lg:px-6">
        <div className="container mx-auto max-w-7xl">
          <div className="flex items-center gap-3 mb-6">
            <BookOpen className="h-7 w-7 text-brand-navy" />
            <div>
              <h1 className="text-2xl lg:text-3xl font-bold font-heading text-slate-800">Internal Documentation</h1>
              <p className="text-xs text-slate-500">Admin-only — operational reference</p>
            </div>
          </div>

          <div className="grid lg:grid-cols-[260px_1fr] gap-6">
            {/* Sidebar */}
            <aside className="lg:sticky lg:top-24 lg:self-start">
              <Card className="p-3" data-testid="docs-sidebar">
                <div className="relative mb-3">
                  <Search className="h-4 w-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                  <Input
                    data-testid="docs-search"
                    placeholder="Search docs"
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    className="pl-8 h-9 text-sm"
                  />
                </div>
                {loadingList ? (
                  <p className="text-xs text-slate-400 px-2 py-2">Loading…</p>
                ) : (
                  <nav className="flex flex-col">
                    {filtered.map(d => (
                      <button
                        key={d.slug}
                        onClick={() => setActiveSlug(d.slug)}
                        data-testid={`docs-nav-${d.slug}`}
                        className={`text-left text-sm px-3 py-2 rounded-md transition-colors flex items-center gap-2 ${
                          activeSlug === d.slug
                            ? 'bg-brand-navy/10 text-brand-navy font-semibold'
                            : 'text-slate-600 hover:bg-slate-50'
                        }`}
                      >
                        <FileText className="h-3.5 w-3.5 flex-shrink-0 opacity-70" />
                        <span className="truncate">{d.title}</span>
                      </button>
                    ))}
                    {filtered.length === 0 && (
                      <p className="text-xs text-slate-400 px-2 py-2">No matches</p>
                    )}
                  </nav>
                )}

                <div className="border-t border-slate-100 mt-3 pt-3 space-y-1">
                  <button
                    type="button"
                    disabled={refreshing}
                    onClick={async () => {
                      setRefreshing(true);
                      setRefreshProgress(0);
                      setRefreshStage('Starting…');
                      // Time-based progress that ramps to 90% over ~5s.
                      // Most of the time is WeasyPrint rendering the PDF (~1–3s on a warm cache,
                      // up to ~10s on a cold cache).
                      const stages = [
                        { at: 0, label: 'Reading source documents…' },
                        { at: 800, label: 'Generating HTML layout…' },
                        { at: 1800, label: 'Rendering PDF pages…' },
                        { at: 4000, label: 'Caching previews…' },
                      ];
                      const startedAt = Date.now();
                      const tick = setInterval(() => {
                        const elapsed = Date.now() - startedAt;
                        const pct = Math.min(90, Math.round((elapsed / 5000) * 90));
                        setRefreshProgress(pct);
                        const stage = [...stages].reverse().find(s => elapsed >= s.at);
                        if (stage) setRefreshStage(stage.label);
                      }, 150);
                      try {
                        const r = await fetch(`${BACKEND_URL}/api/admin/docs/refresh`, {
                          method: 'POST',
                          credentials: 'include',
                          headers: authHeaders,
                        });
                        if (!r.ok) {
                          const err = await r.json().catch(() => ({}));
                          throw new Error(err.detail || `HTTP ${r.status}`);
                        }
                        const data = await r.json();
                        clearInterval(tick);
                        setRefreshProgress(100);
                        setRefreshStage(`Complete — ${data.pages} pages in ${data.duration_seconds}s`);
                        toast.success(`Documentation refreshed (${data.pages} pages)`);
                        // Re-open viewer if it was already open, with cache-busting
                        if (pdfPages > 0) setPdfPages(0);
                        setTimeout(() => {
                          setRefreshing(false);
                          setRefreshProgress(0);
                          setRefreshStage('');
                        }, 1500);
                      } catch (e) {
                        clearInterval(tick);
                        setRefreshProgress(0);
                        setRefreshStage('');
                        setRefreshing(false);
                        toast.error(`Refresh failed: ${e.message}`);
                      }
                    }}
                    data-testid="docs-refresh-btn"
                    className="w-full text-xs flex items-center gap-2 px-3 py-2 rounded-md text-brand-sage hover:bg-brand-sage/10 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    <RefreshCw className={`h-3.5 w-3.5 ${refreshing ? 'animate-spin' : ''}`} />
                    <span className="truncate text-left">{refreshing ? 'Refreshing…' : 'Refresh from latest docs'}</span>
                  </button>

                  <button
                    type="button"
                    onClick={async () => {
                      const t = toast.loading('Loading PDF…');
                      try {
                        const r = await fetch(`${BACKEND_URL}/api/admin/docs/features/info`, {
                          credentials: 'include',
                          headers: authHeaders,
                        });
                        if (!r.ok) throw new Error(`HTTP ${r.status}`);
                        const info = await r.json();
                        setPdfPages(info.pages || 0);
                        toast.success(`Showing ${info.pages} pages`, { id: t });
                      } catch (e) {
                        toast.error(`Could not load PDF: ${e.message}`, { id: t });
                      }
                    }}
                    data-testid="docs-download-features-pdf"
                    className="w-full text-xs flex items-center gap-2 px-3 py-2 rounded-md text-brand-navy hover:bg-brand-navy/5 transition-colors"
                  >
                    <Download className="h-3.5 w-3.5" />
                    <span className="truncate text-left">Features &amp; Functionality (PDF)</span>
                  </button>
                </div>
              </Card>
            </aside>

            {/* Content */}
            <article>
              {refreshing && (
                <Card className="p-4 mb-4 border-brand-sage/40 bg-brand-sage/5" data-testid="docs-refresh-progress">
                  <div className="flex items-center gap-3 mb-2">
                    <RefreshCw className="h-4 w-4 text-brand-sage animate-spin flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-slate-800">Refreshing documentation</p>
                      <p className="text-xs text-slate-500 truncate">{refreshStage || 'Starting…'}</p>
                    </div>
                    <span className="text-xs font-semibold text-brand-sage tabular-nums">{refreshProgress}%</span>
                  </div>
                  <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-brand-navy to-brand-sage transition-all duration-300 ease-out"
                      style={{ width: `${refreshProgress}%` }}
                    />
                  </div>
                  <p className="text-[11px] text-slate-400 mt-2">This typically takes 2–5 seconds. Please leave this page open.</p>
                </Card>
              )}
              <Card className="p-6 lg:p-10" data-testid="docs-content">
                {error && <p className="text-sm text-red-600 mb-4">{error}</p>}
                {loadingDoc ? (
                  <p className="text-sm text-slate-400">Loading…</p>
                ) : (
                  <div className="prose prose-slate max-w-none prose-headings:font-heading prose-h1:text-3xl prose-h2:text-xl prose-h3:text-base prose-table:text-sm">
                    <ReactMarkdown
                      remarkPlugins={[remarkGfm]}
                      components={{
                        a: ({ href, children, ...props }) => {
                          // Internal cross-doc links: ./07-frontend.md → ?doc=07-frontend
                          if (typeof href === 'string' && /^\.?\/?\d{2}-[a-z-]+\.md(#.*)?$/i.test(href)) {
                            const slug = href.replace(/^\.?\/?/, '').replace(/\.md.*$/i, '');
                            return (
                              <a
                                href={`/docs?doc=${slug}`}
                                onClick={(e) => { e.preventDefault(); setActiveSlug(slug); }}
                                {...props}
                              >
                                {children}
                              </a>
                            );
                          }
                          return (
                            <a href={href} target={href?.startsWith('http') ? '_blank' : undefined}
                               rel={href?.startsWith('http') ? 'noopener noreferrer' : undefined}
                               {...props}>{children}</a>
                          );
                        },
                        table: ({ children, ...props }) => (
                          <div className="overflow-x-auto"><table {...props}>{children}</table></div>
                        ),
                        // Inline code → light pill; block code (inside <pre>) → leave styling to <pre>
                        code: ({ inline, className, children, ...props }) => {
                          if (inline) {
                            return (
                              <code
                                className="bg-slate-100 text-slate-800 rounded px-1 py-0.5 text-[0.85em] font-mono before:content-none after:content-none"
                                {...props}
                              >
                                {children}
                              </code>
                            );
                          }
                          return (
                            <code className={`${className || ''} text-slate-100 font-mono text-[12px] leading-relaxed whitespace-pre`} {...props}>
                              {children}
                            </code>
                          );
                        },
                        pre: ({ children, ...props }) => (
                          <pre
                            className="bg-slate-900 text-slate-100 p-4 rounded-lg overflow-x-auto"
                            {...props}
                          >
                            {children}
                          </pre>
                        ),
                      }}
                    >
                      {content}
                    </ReactMarkdown>
                  </div>
                )}
              </Card>
            </article>
          </div>
        </div>
      </main>

      {pdfPages > 0 && (
        <PdfPagesViewer
          pages={pdfPages}
          onClose={() => setPdfPages(0)}
          authHeaders={authHeaders}
          backendUrl={BACKEND_URL}
        />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Renders the multi-page PDF viewer. Each page is fetched via authenticated
// fetch and shown as a blob URL — this avoids relying on third-party cookies
// inside the Emergent preview iframe (which Chrome may strip).
// ─────────────────────────────────────────────────────────────────────────────
function PdfPagesViewer({ pages, onClose, authHeaders, backendUrl }) {
  const [pageUrls, setPageUrls] = useState({});
  const [errors, setErrors] = useState({});

  useEffect(() => {
    let cancelled = false;
    const created = [];
    (async () => {
      for (let p = 1; p <= pages; p++) {
        if (cancelled) break;
        try {
          const r = await fetch(`${backendUrl}/api/admin/docs/features/page/${p}`, {
            credentials: 'include',
            headers: authHeaders,
          });
          if (!r.ok) throw new Error(`HTTP ${r.status}`);
          const blob = await r.blob();
          const url = URL.createObjectURL(blob);
          created.push(url);
          if (!cancelled) {
            setPageUrls(prev => ({ ...prev, [p]: url }));
          }
        } catch (e) {
          if (!cancelled) {
            setErrors(prev => ({ ...prev, [p]: e.message || 'Failed' }));
          }
        }
      }
    })();
    return () => {
      cancelled = true;
      created.forEach(u => URL.revokeObjectURL(u));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pages]);

  return (
    <div
      className="fixed inset-0 z-50 bg-slate-900/90 backdrop-blur-sm flex flex-col"
      data-testid="pdf-viewer-overlay"
    >
      <div className="flex items-center justify-between px-4 py-2.5 bg-slate-900 text-white border-b border-slate-800">
        <div className="flex items-center gap-2 text-sm">
          <FileText className="h-4 w-4 text-brand-sage" />
          <span className="font-semibold">Features &amp; Functionality</span>
          <span className="text-slate-400 hidden sm:inline">— {pages} pages</span>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="text-xs px-3 py-1.5 rounded-md bg-slate-700 hover:bg-slate-600 text-white"
          data-testid="pdf-viewer-close"
        >
          Close
        </button>
      </div>
      <div className="flex-1 overflow-y-auto bg-slate-800 px-4 py-6">
        <div className="max-w-4xl mx-auto space-y-4">
          {Array.from({ length: pages }, (_, i) => i + 1).map(p => (
            <div key={p} className="bg-white shadow-2xl rounded-sm overflow-hidden" data-testid={`pdf-page-${p}`}>
              {pageUrls[p] ? (
                <img src={pageUrls[p]} alt={`Page ${p}`} className="w-full block" />
              ) : errors[p] ? (
                <div className="aspect-[1/1.414] flex items-center justify-center text-sm text-red-500 p-8 text-center">
                  Could not load page {p} ({errors[p]}).
                </div>
              ) : (
                <div className="aspect-[1/1.414] flex items-center justify-center text-sm text-slate-400">
                  Loading page {p}…
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
